import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Check, Calendar, ExternalLink, Info, Loader2,
  Shield, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import SEOHead, { generateBreadcrumbSchema, generateSoftwareApplicationSchema } from '@/components/SEOHead';
import ClaimDiscountButton from '@/components/ClaimDiscountButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dedicated storefront for a Featured AI Tool (affiliate partner).
 *
 * Route: /featured/:slug  (e.g. /featured/marblism)
 *
 * Renders a rich, branded landing page on theailedger.ai so visitors can
 * actually SEE the partner as a first-class tenant of our marketplace
 * before the final outbound click. The big CTA still routes through our
 * click tracker at /api/affiliate-partners/go/:slug so attribution stays
 * intact — this page is the gateway, not a bypass.
 *
 * Also loads up to 5 other featured tools for cross-discovery at the
 * bottom, so one featured partner surfaces the rest.
 */
export default function AffiliatePartnerStore() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [others, setOthers] = useState([]);
  // Weekly-digest backlinks — every published issue that listed this
  // partner. Lets visitors browse "what else was hot the week this tool
  // was featured" without leaving the site (compounds dwell time + SEO).
  const [weeklyIssues, setWeeklyIssues] = useState([]);
  const [page, setPage] = useState(0);
  const PER_PAGE = 9;
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPage(0);
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const [p, all, weekly] = await Promise.all([
          axios.get(`${API_URL}/api/affiliate-partners/${slug}`),
          axios.get(`${API_URL}/api/affiliate-partners?limit=500`),
          axios.get(`${API_URL}/api/weekly/by-item/${slug}`).catch(() => ({ data: { issues: [] } })),
        ]);
        if (cancelled) return;
        setPartner(p.data);
        // All other featured tools, paginated below to prevent endless scroll.
        setOthers(
          (all.data.partners || []).filter((x) => x.slug !== slug)
        );
        setWeeklyIssues(weekly.data?.issues || []);

        // Fire-and-forget retargeting view track. Mirrors the product
        // view tracking on /product/:id so logged-in visitors who don't
        // click the affiliate link get the 1h + 24h reminder emails.
        // Anonymous visitors are skipped server-side.
        axios.post(`${API_URL}/api/retargeting/track-partner-view`, { partner_slug: slug })
          .catch((e) => console.debug('Partner view track failed (non-fatal):', e?.message || e));
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const goUrl = partner ? `${API_URL}${partner.go_path}` : null;
  const openPartner = () => {
    if (goUrl) window.open(goUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  if (notFound || !partner) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-8">
        <Sparkles className="w-12 h-12 text-neutral-700 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Featured tool not found</h1>
        <p className="text-neutral-400 mb-6">This partner doesn&rsquo;t exist or is no longer active.</p>
        <Button onClick={() => navigate('/partners')} className="bg-[#00F0FF] text-black hover:bg-[#00C2CC]">
          Browse all partners →
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEOHead
        title={`${partner.name} on theailedger.ai — Featured AI Tool`}
        description={partner.tagline || partner.description?.slice(0, 160)}
        path={`/featured/${partner.slug}`}
        keywords={`${partner.name}, ${partner.category}, AI tools, theailedger.ai featured partner`}
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            generateSoftwareApplicationSchema(partner),
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Featured AI Tools', path: '/featured' },
              ...(partner.category
                ? [{ name: partner.category, path: `/featured?category=${encodeURIComponent(partner.category)}` }]
                : []),
              { name: partner.name, path: `/featured/${partner.slug}` },
            ]),
          ].filter(Boolean),
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-40">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <Logo />
          </button>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-neutral-400 hover:text-[#00F0FF] inline-flex items-center gap-1.5"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,240,255,0.1),transparent_60%)]" />
        <div className="relative container mx-auto px-6 md:px-12 py-14 md:py-20 max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
            <span className="text-xs text-[#00F0FF] font-semibold tracking-wider">FEATURED AI TOOL</span>
          </div>

          <div className="flex items-start gap-5 md:gap-8 mb-8">
            <div className="shrink-0 w-20 h-20 md:w-28 md:h-28 rounded-2xl bg-black/60 border border-white/10 flex items-center justify-center overflow-hidden p-2">
              {partner.logo_url ? (
                <img
                  src={partner.logo_url}
                  alt={partner.name}
                  className="w-full h-full object-contain"
                  data-testid="partner-store-logo"
                />
              ) : (
                <Sparkles className="w-10 h-10 text-[#00F0FF]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-2 leading-tight" data-testid="partner-store-name">
                {partner.name}
              </h1>
              {partner.tagline && (
                <p className="text-base md:text-lg text-neutral-300 mb-3">{partner.tagline}</p>
              )}
              {partner.website_display && (
                <span className="inline-flex items-center gap-1 text-sm text-neutral-500">
                  {partner.website_display}
                </span>
              )}
            </div>
          </div>

          {partner.customer_benefit && (
            <div className="inline-flex items-center gap-2 bg-[#00FF94]/10 border border-[#00FF94]/30 rounded-full px-4 py-2 mb-8" data-testid="partner-customer-benefit">
              <Check className="w-4 h-4 text-[#00FF94]" />
              <span className="text-sm font-semibold text-[#00FF94]">
                theailedger.ai customers: {partner.customer_benefit}
              </span>
            </div>
          )}

          {weeklyIssues.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="partner-weekly-backlinks">
              <span className="text-xs text-neutral-500 uppercase tracking-wider font-semibold mr-1">
                Featured in
              </span>
              {weeklyIssues.slice(0, 4).map((iss) => (
                <Link
                  key={iss.slug}
                  to={`/weekly/${iss.slug}`}
                  className="inline-flex items-center gap-1.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#00F0FF]/40 rounded-full px-3 py-1.5 text-xs text-neutral-300 hover:text-white transition-colors"
                  data-testid={`partner-weekly-link-${iss.slug}`}
                >
                  <Calendar className="w-3 h-3 text-[#00F0FF]" />
                  Week {String(iss.iso_week).padStart(2, '0')}, {iss.iso_year}
                </Link>
              ))}
              {weeklyIssues.length > 4 && (
                <Link
                  to="/weekly"
                  className="text-xs text-neutral-500 hover:text-[#00F0FF]"
                  data-testid="partner-weekly-more"
                >
                  +{weeklyIssues.length - 4} more
                </Link>
              )}
            </div>
          )}

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            {partner.has_private_discount ? (
              <ClaimDiscountButton partner={partner} />
            ) : (
              <Button
                onClick={openPartner}
                className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold h-14 px-8 text-base rounded-xl"
                data-testid="partner-store-primary-cta"
              >
                Get {partner.name}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
            {partner.has_private_discount && (
              <button
                onClick={openPartner}
                className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"
                data-testid="partner-store-skip-discount"
              >
                <ExternalLink className="w-3 h-3" />
                Skip — just visit {partner.website_display || partner.name}
              </button>
            )}
            {!partner.has_private_discount && (
              <span className="text-xs text-neutral-500 sm:ml-2 inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Opens {partner.website_display || 'partner website'} in a new tab
              </span>
            )}
          </div>
        </div>
      </section>

      {/* About */}
      <main className="container mx-auto px-6 md:px-12 py-12 md:py-16 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-2 space-y-5">
            <h2 className="text-xl md:text-2xl font-bold mb-3">About {partner.name}</h2>
            <p className="text-neutral-300 leading-relaxed whitespace-pre-line" data-testid="partner-store-description">
              {partner.description}
            </p>
          </div>
          <aside className="space-y-3">
            <div className="rounded-xl border border-white/10 bg-neutral-900/50 p-4">
              <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Category</p>
              <p className="text-sm font-semibold">{partner.category}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-neutral-900/50 p-4">
              <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Status</p>
              <p className="text-sm font-semibold text-[#00FF94] inline-flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" /> Verified partner
              </p>
            </div>
            {partner.customer_benefit && (
              <div className="rounded-xl border border-[#00FF94]/20 bg-[#00FF94]/5 p-4">
                <p className="text-[10px] uppercase tracking-wider text-[#00FF94]/80 mb-1">Customer perk</p>
                <p className="text-sm font-semibold">{partner.customer_benefit}</p>
              </div>
            )}
          </aside>
        </div>

        {/* Secondary CTA */}
        <div className="mt-14 rounded-2xl bg-[#00F0FF]/5 border border-[#00F0FF]/30 p-8 md:p-10 text-center">
          <Sparkles className="w-10 h-10 text-[#00F0FF] mx-auto mb-3" />
          <h3 className="text-2xl md:text-3xl font-black mb-2">
            Ready to try {partner.name}?
          </h3>
          {partner.customer_benefit && (
            <p className="text-sm text-[#00FF94] mb-5">
              {partner.customer_benefit} when you sign up through theailedger.ai
            </p>
          )}
          <Button
            onClick={openPartner}
            className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold h-12 px-8 rounded-xl"
            data-testid="partner-store-secondary-cta"
          >
            Visit {partner.name} →
          </Button>
        </div>

        {/* FTC disclosure — clear and conspicuous */}
        <div className="mt-8 flex items-start gap-2 text-xs text-neutral-500" data-testid="partner-store-disclosure">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <p>
            theailedger.ai may earn a commission if you sign up for {partner.name} through this
            page, at no extra cost to you. We only feature tools we&rsquo;d independently recommend.
          </p>
        </div>

        {/* Cross-discovery */}
        {others.length > 0 && (() => {
          const totalPages = Math.max(1, Math.ceil(others.length / PER_PAGE));
          const safePage = Math.min(page, totalPages - 1);
          const visible = others.slice(safePage * PER_PAGE, (safePage + 1) * PER_PAGE);
          return (
            <div className="mt-20" data-testid="more-featured-section">
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <Sparkles className="w-4 h-4 text-[#00F0FF]" />
                <h2 className="text-lg md:text-xl font-bold">More featured AI tools</h2>
                <button
                  onClick={() => navigate('/partners')}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00F0FF] hover:text-[#00F0FF]/80 border border-[#00F0FF]/30 hover:border-[#00F0FF]/60 rounded-full px-3 py-1.5 transition-colors"
                  data-testid="more-featured-tools-btn"
                >
                  See all
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <div className="flex-1 h-px bg-white/5" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visible.map((o) => (
                  <button
                    key={o.slug}
                    onClick={() => navigate(`/featured/${o.slug}`)}
                    className="group text-left rounded-xl bg-neutral-900/50 border border-white/10 hover:border-[#00F0FF]/40 transition-colors p-4 flex items-center gap-3"
                    data-testid={`other-featured-${o.slug}`}
                  >
                    <div className="shrink-0 w-11 h-11 rounded-lg bg-black/40 border border-white/5 flex items-center justify-center overflow-hidden p-1">
                      {o.logo_url ? (
                        <img src={o.logo_url} alt={o.name} className="w-full h-full object-contain" loading="lazy" />
                      ) : (
                        <Sparkles className="w-4 h-4 text-[#00F0FF]" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate group-hover:text-[#00F0FF] transition-colors">
                        {o.name}
                      </p>
                      <p className="text-xs text-neutral-500 truncate">
                        {o.tagline || o.category}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between gap-3" data-testid="more-featured-pagination">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={safePage === 0}
                    className="inline-flex items-center gap-1.5 text-sm text-neutral-300 hover:text-white disabled:text-neutral-600 disabled:cursor-not-allowed transition-colors"
                    data-testid="more-featured-prev-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="text-xs text-neutral-500" data-testid="more-featured-page-indicator">
                    Page {safePage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={safePage >= totalPages - 1}
                    className="inline-flex items-center gap-1.5 text-sm text-neutral-300 hover:text-white disabled:text-neutral-600 disabled:cursor-not-allowed transition-colors"
                    data-testid="more-featured-next-btn"
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </main>
    </div>
  );
}
